import React, { RefObject } from "react";
import {
  OptionalBenefitPackage,
  Plan,
  PlanDetailsPageSection,
} from "../../@types";
import { RenderedTable } from "../../components";
import { CONSTANTS as C, useTranslate } from "../../helpers";
import { PlanDetailSection } from "./components";

export const makeOptionalPackageNumberLabel = (
  label: string,
  number: number
): string => `${label} #${number}`;

const OptionalPackagesSection: React.FunctionComponent<{
  plan: Plan;
  sectionRef: RefObject<HTMLDivElement>;
}> = ({ plan, sectionRef }) => {
  const t = useTranslate();

  const getBenefitCommaList = (benefits: string[]): string => {
    if (benefits.length > 1) {
      const head = benefits
        .map(benefit => t(C.optionalPackageBenefits[benefit]))
        .slice(0, benefits.length - 1)
        .join(", ");
      const tail = benefits
        .map(benefit => t(C.optionalPackageBenefits[benefit]))
        .slice(benefits.length - 1);

      return `${head}, and ${tail}`;
    }

    return t(C.optionalPackageBenefits[benefits[0]]);
  };
  const optionalBenefits: OptionalBenefitPackage[] = plan.optional_benefits;
  const columns = [{ accessor: "packageInfo" }, { accessor: "packageDetails" }];
  const packageSorter = (
    pkgA: OptionalBenefitPackage,
    pkgB: OptionalBenefitPackage
  ) => pkgA.package_number - pkgB.package_number;
  // Store mutated (sorted) copy of optionalBenefits for creating table data
  const sortedOptionalBenefits = [...optionalBenefits].sort(packageSorter);
  const data = sortedOptionalBenefits.map(pkg => {
    return {
      packageInfo: (
        <>
          <b>
            {makeOptionalPackageNumberLabel(
              t("optional_packages.package"),
              pkg.package_number
            )}
          </b>{" "}
          {t("optional_packages.includes")} {getBenefitCommaList(pkg.benefits)}
        </>
      ),
      packageDetails: (
        <ul className="ds-c-list ds-c-list--bare">
          <li>
            <b>{t("optional_packages.monthly_premium")}</b>:{" "}
            {pkg.monthly_premium}
          </li>
          <li>
            <b>{t("optional_packages.deductible")}</b>: {pkg.deductible}
          </li>
        </ul>
      ),
    };
  });

  return (
    <PlanDetailSection
      id={PlanDetailsPageSection.OPTIONAL_PACKAGES}
      sectionRef={sectionRef}
      title={t("optional_packages.title")}
    >
      <RenderedTable
        caption={<div>{t("optional_packages.description")}</div>}
        className="OptionalPackagesTable"
        rowHeaders={false}
        tableColumns={columns}
        tableData={data}
      />
    </PlanDetailSection>
  );
};
export { OptionalPackagesSection };
